// src/pages/preAccounting/components/PreAccountingAddDialog.jsx
import React, { useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';

const Z_FIX = { zIndex: 2147483647 };

const currencyOptions = [
  { label: 'CAD', value: 'CAD' },
  { label: 'CHF', value: 'CHF' },
  { label: 'DKK', value: 'DKK' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'JPY', value: 'JPY' },
  { label: 'NOK', value: 'NOK' },
  { label: 'SAR', value: 'SAR' },
  { label: 'SEK', value: 'SEK' },
  { label: 'USD', value: 'USD' },
  { label: 'TRY', value: 'TRY' }
];

const PreAccountingAddDialog = ({ th, visible, onHide, form, setForm, onSave, options, loading, isEditMode = false }) => {
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const browserLocale = navigator.language || 'tr-TR';

  // options geldiğinde ve alanlar boşsa default'ları kur
  useEffect(() => {
    if (!visible) return;
    const hasOpts =
      (options?.paymentTypes?.length ?? 0) > 0 &&
      (options?.transactionTypes?.length ?? 0) > 0 &&
      (options?.typeOfMovements?.length ?? 0) > 0;

    if (!hasOpts) return;

    const needPT = form.paymentType == null;
    const needTT = form.transactionType == null;
    const needMT = form.processType == null;

    if (needPT || needTT || needMT) {
      setForm((f) => ({
        ...f,
        paymentType: f.paymentType ?? options.paymentTypes[0].value,
        transactionType: f.transactionType ?? options.transactionTypes[0].value,
        processType: f.processType ?? options.typeOfMovements[0].value
      }));
    }
  }, [visible, options, form.paymentType, form.transactionType, form.processType, setForm]);

  return (
    <Dialog
      header={isEditMode ? th('preAccounting.editTitle') : th('preAccounting.title')}
      visible={visible}
      style={{ width: 900, maxWidth: 900 }}
      modal
      onHide={onHide}
      closable
      contentClassName="p-3"
      baseZIndex={1000}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label={th('preAccounting.cancel')} icon="pi pi-times" className="p-button-text" onClick={onHide} />
          <Button label={isEditMode ? th('preAccounting.update') : th('preAccounting.save')} icon="pi pi-check" onClick={onSave} />
        </div>
      }
    >
      <div className="grid" style={{ rowGap: 12 }}>
        {/* Satır 1: Ödeme Tipi – İşlem Tipi */}
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.paymentType')}</label>
          <Dropdown
            value={form.paymentType}
            options={options?.paymentTypes ?? []}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => set('paymentType', e.value)}
            placeholder={th('preAccounting.placeholders.paymentType')}
            className="w-full"
            loading={loading}
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.transactionType')}</label>
          <Dropdown
            value={form.transactionType}
            options={options?.transactionTypes ?? []}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => set('transactionType', e.value)}
            placeholder={th('preAccounting.placeholders.transactionType')}
            className="w-full"
            loading={loading}
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>

        {/* Satır 2: Hareket Tipi – Para Birimi */}
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.processType')}</label>
          <Dropdown
            value={form.processType}
            options={options?.typeOfMovements ?? []}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => set('processType', e.value)}
            placeholder={th('preAccounting.placeholders.processType')}
            className="w-full"
            loading={loading}
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.currency')}</label>
          <Dropdown
            value={form.currency}
            options={currencyOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => set('currency', e.value)}
            placeholder={th('preAccounting.placeholders.currency')}
            className="w-full"
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>

        {/* Satır 3: Miktar – İşlem Tarihi */}
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.amount')}</label>
          <InputNumber
            value={form.amount}
            onValueChange={(e) => set('amount', e.value ?? null)}
            min={0}
            mode="decimal"
            maxFractionDigits={2}
            locale={browserLocale}
            className="w-full"
            placeholder={th('preAccounting.placeholders.amount')}
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.transactionDate')}</label>
          <Calendar
            value={form.transactionDate ? new Date(form.transactionDate) : null}
            onChange={(e) => set('transactionDate', e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            placeholder={th('preAccounting.placeholders.transactionDate')}
            className="w-full"
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>

        {/* Satır 4: Vade Tarihi – Evrak No */}
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.dueDate')}</label>
          <Calendar
            value={form.dueDate ? new Date(form.dueDate) : null}
            onChange={(e) => set('dueDate', e.value)}
            dateFormat="dd/mm/yy"
            showIcon
            placeholder={th('preAccounting.placeholders.dueDate')}
            className="w-full"
            appendTo={document.body}
            panelStyle={Z_FIX}
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.documentNo')}</label>
          <InputText
            value={form.documentNo ?? ''}
            onChange={(e) => set('documentNo', e.target.value)}
            className="w-full"
            placeholder={th('preAccounting.placeholders.documentNo')}
          />
        </div>

        {/* Satır 5: Evrak Seri No – Açıklama */}
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.documentSeriesNo')}</label>
          <InputText
            value={form.documentSeriesNo ?? ''}
            onChange={(e) => set('documentSeriesNo', e.target.value)}
            className="w-full"
            placeholder={th('preAccounting.placeholders.documentSeriesNo')}
          />
        </div>
        <div className="col-12 md:col-6">
          <label className="block mb-2">{th('preAccounting.description')}</label>
          <InputText
            value={form.description ?? ''}
            onChange={(e) => set('description', e.target.value)}
            className="w-full"
            placeholder={th('preAccounting.placeholders.description')}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PreAccountingAddDialog;
